import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { oneOf, func } from "prop-types";
import { Map, List } from "immutable";
import Cookies from "universal-cookie";
import i18n from "@/i18n";

import SignInBankId from "../../dumb-components/credentials/middle-components/sign-in-bankid";
import SignInEmail from "../../dumb-components/credentials/middle-components/sign-in-email";

import Alert from "@/components/alert";

import {
  authBankId,
  resetCredentials,
} from "../../actions/credentials.actions";

import { validateEmail } from "../../modules/validation.module";

import {
  EMAIL_FIELD,
  PASSWORD_FIELD,
  REMEMBER_DETAILS_FIELD,
  CODE_FIELD,
} from "../../constants/credentials";
import { Trans } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Undo2 } from "lucide-react";

const { t } = i18n;

const SignInContainer = ({
  authMode,
  onChangeAuthMode,
  onForgotPassword,
  onChange,
  step,
  translationId,
  status,
  sessionId,
  authBankId,
  resetCredentials,
}) => {
  const [isEmailFormActive, setIsEmailFormActive] = useState(false);

  const [formData, setFormData] = useState(Map());
  const [hasError, setHasError] = useState(Map());
  const [actionBtnLoading, setActionBtnLoading] = useState(false);
  const [signInMethod, setSignInMethod] = useState("");

  const initState = (init) => {
    let rememberDetailsBankId = new Cookies().get("rememberDetailsBankId");
    let rememberDetailsEmail = new Cookies().get("rememberDetailsEmail");

    if (init && rememberDetailsEmail && authMode !== 0) {
      onChangeAuthMode(0);
      return;
    }

    rememberDetailsBankId = rememberDetailsBankId || Map();
    rememberDetailsEmail = rememberDetailsEmail || Map();

    setHasError(Map());
    setFormData(
      authMode === 1 ? Map(rememberDetailsBankId) : Map(rememberDetailsEmail),
    );
    setActionBtnLoading(false);
  };

  useEffect(() => {
    initState(true);
    document.title = "INVONO One™ | Login med BankID";
  }, []);

  useEffect(() => {
    initState();
  }, [authMode]);

  const handleChange = (fieldName, value) => {
    let currentFormData = formData;

    if (
      currentFormData.get("useRememberDetails") &&
      fieldName !== PASSWORD_FIELD &&
      fieldName !== CODE_FIELD
    ) {
      if (currentFormData.get("userId")) {
        new Cookies().remove("rememberDetailsBankId");
      } else {
        new Cookies().remove("rememberDetailsEmail");
      }

      currentFormData = currentFormData.set(REMEMBER_DETAILS_FIELD, false);
      currentFormData = currentFormData.remove("useRememberDetails");
      currentFormData = currentFormData.remove("userId");
    }

    if (fieldName === CODE_FIELD) {
      value = value.trim();
    }

    currentFormData = currentFormData.set(fieldName, value);

    setFormData(currentFormData);

    onChange?.(fieldName, value);
  };

  const onSignInBankId = () => {
    setActionBtnLoading(true);
    setSignInMethod("bankid");

    authBankId("SIGNIN", sessionId, "SIGNIN-BANKID", formData, () => {
      setActionBtnLoading(false);
    });
  };

  const onSignInEmail = () => {
    if (!isEmailFormActive) {
      setIsEmailFormActive(true);
      return;
    }
    if (validateSignInEmail()) {
      setActionBtnLoading(true);
      setSignInMethod("email");

      authBankId("SIGNIN", sessionId, "SIGNIN-BASIC", formData, () => {
        setActionBtnLoading(false);
      });
    }
  };

  const validateSignInEmail = () => {
    let errorMap = Map();
    let isValid = true;

    // Validate Email
    if (!validateEmail(formData.get(EMAIL_FIELD))) {
      errorMap = errorMap.set(
        EMAIL_FIELD,
        List(["credentials.validation.email"]),
      );
      isValid = false;
    }

    // Check that password was entered
    if (formData.get(PASSWORD_FIELD, "").length === 0) {
      errorMap = errorMap.set(
        PASSWORD_FIELD,
        List(["credentials.validation.password_empty"]),
      );
      isValid = false;
    }

    setHasError(errorMap);

    return isValid;
  };

  const validateCode = () => {
    let errorMap = Map();
    let isValid = true;
    const codeValue = formData.get(CODE_FIELD, "");
    const hasNumbersOnly = /^\d+$/.test(codeValue);

    // Validate Code
    if (!hasNumbersOnly) {
      errorMap = errorMap.set(
        CODE_FIELD,
        List(["credentials.validation.code"]),
      );
      isValid = false;
    }

    setHasError(errorMap);

    return isValid;
  };

  const onClickVerify = () => {
    if (validateCode()) {
      setActionBtnLoading(true);

      authBankId("SIGNIN", sessionId, "SIGNIN-BASIC", formData, () => {
        setActionBtnLoading(false);
      });
    }
  };

  const onCancelEmailVerify = () => {
    resetCredentials();
    initState(); // Needed to check details in the cookie
  };

  return (
    <div className="flex flex-col">
      <h1 className="mb-10 text-2xl">
        <Trans i18nKey="credentials_sign_in_title" />
      </h1>
      {translationId && (
        <Alert
          title={t(
            status === "failed"
              ? "notifications.title.ERROR"
              : "notifications.title.INFO",
          )}
          message={t(translationId)}
          variant={status === "failed" ? "destructive" : undefined}
        />
      )}
      <div className="flex flex-col gap-6">
        {isEmailFormActive ? (
          <div>
            <Button variant="link" onClick={() => setIsEmailFormActive(false)}>
              <Undo2 />
              {t("generic.back")}
            </Button>
          </div>
        ) : (
          <SignInBankId
            onClickSignIn={onSignInBankId}
            onChange={handleChange}
            actionBtnLoading={actionBtnLoading && signInMethod === "bankid"}
          />
        )}
        <SignInEmail
          onClickSignIn={onSignInEmail}
          onClickForgotPassword={onForgotPassword}
          onChange={handleChange}
          onClickCancel={onCancelEmailVerify}
          onClickVerify={onClickVerify}
          hasError={hasError}
          isVerifyMode={step > 0}
          actionBtnLoading={actionBtnLoading && signInMethod === "email"}
          email={formData && formData.get(EMAIL_FIELD)}
          password={formData && formData.get(PASSWORD_FIELD)}
          code={formData && formData.get(CODE_FIELD)}
          rememberDetails={formData && formData.get(REMEMBER_DETAILS_FIELD)}
          isFormActive={isEmailFormActive}
        />
      </div>
    </div>
  );
};

SignInContainer.propTypes = {
  authMode: oneOf([0, 1]), // 0 = EMAIL / 1 = BANKID
  onChangeAuthMode: func,
  onForgotPassword: func,
  onChange: func,
};

function mapStoreToProps(store) {
  return {
    step: store.credentials.get("step"),
    translationId: store.credentials.get("translationId"),
    status: store.credentials.get("status"),
    sessionId: store.credentials.get("sessionId"),
  };
}

const mapActionsToProps = {
  authBankId,
  resetCredentials,
};

export default connect(mapStoreToProps, mapActionsToProps)(SignInContainer);
